import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Receipt, FileText, AlertCircle, TrendingUp, Clock, Copy, Phone, UserCheck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const TOOLTIP_STYLE = { background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '4px', color: 'hsl(var(--foreground))', fontSize: '12px' };
const AXIS_TICK = { fontSize: 12, fill: 'hsl(var(--muted-foreground))' };
const BAR_RADIUS = [2, 2, 0, 0];
const CHART_HEIGHT = 240;

function getVariantClass(variant, value) {
  if (variant === 'destructive' && value > 0) return 'border-destructive/30';
  if (variant === 'warning' && value > 0) return 'border-amber-500/30';
  return '';
}

function getIconClass(variant, value) {
  if (variant === 'destructive' && value > 0) return 'text-destructive';
  if (variant === 'warning' && value > 0) return 'text-amber-500';
  return 'text-muted-foreground';
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(() => {
    api.get('/dashboard/stats')
      .then(res => setStats(res.data))
      .catch(err => void 0)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const s = useMemo(() => stats || {
    total_receipts: 0, today_count: 0, total_files: 0, error_count: 0,
    duplicate_count: 0, pending_users: 0, top_stores: [], monthly_data: [],
    recent_receipts: [], sender_stats: []
  }, [stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-6 h-6 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">Receipt automation overview</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="stats-grid">
        <StatCard icon={Receipt} label="Total Receipts" value={s.total_receipts} testId="stat-total-receipts" />
        <StatCard icon={Clock} label="Today" value={s.today_count} testId="stat-today" />
        <StatCard icon={FileText} label="Total Files" value={s.total_files} testId="stat-total-files" />
        <StatCard icon={Copy} label="Duplicates" value={s.duplicate_count || 0} variant={s.duplicate_count > 0 ? "warning" : undefined} testId="stat-duplicates" />
        {user?.role === 'admin' && (
          <StatCard icon={UserCheck} label="Pending Users" value={s.pending_users || 0} variant={s.pending_users > 0 ? "warning" : undefined} testId="stat-pending-users" />
        )}
        <StatCard icon={AlertCircle} label="Errors" value={s.error_count} variant="destructive" testId="stat-errors" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border rounded-md p-6" data-testid="monthly-chart">
          <h3 className="text-sm font-semibold mb-4 uppercase tracking-wider text-muted-foreground">Monthly Receipts</h3>
          {s.monthly_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <BarChart data={s.monthly_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="hsl(var(--accent))" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-muted-foreground text-sm">No data yet.</div>
          )}
        </div>
        <div className="border border-border rounded-md p-6" data-testid="top-stores">
          <h3 className="text-sm font-semibold mb-4 uppercase tracking-wider text-muted-foreground">Top Stores</h3>
          {s.top_stores.length > 0 ? (
            <div className="space-y-3">
              {s.top_stores.map((store) => (
                <div key={store.name} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="text-sm truncate">{store.name}</span>
                  <span className="font-mono text-sm text-muted-foreground">{store.count}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No stores yet</p>}
        </div>
      </div>

      <div className="border border-border rounded-md" data-testid="sender-stats">
        <div className="p-6 border-b border-border flex items-center gap-2">
          <Phone className="w-4 h-4 text-[hsl(var(--accent))]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Approved Senders Activity</h3>
        </div>
        {(s.sender_stats || []).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="text-left px-6 py-2.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Phone</th>
                  <th className="text-left px-6 py-2.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Name</th>
                  <th className="text-right px-6 py-2.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Invoices</th>
                  <th className="text-right px-6 py-2.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Duplicates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(s.sender_stats || []).map((sender) => (
                  <tr key={sender.phone_number} className="hover:bg-secondary/30 transition-colors">
                    <td className="px-6 py-3 text-sm font-mono">{sender.phone_number}</td>
                    <td className="px-6 py-3 text-sm">{sender.name || '—'}</td>
                    <td className="px-6 py-3 text-sm font-mono text-right font-semibold">{sender.invoices_sent}</td>
                    <td className="px-6 py-3 text-sm font-mono text-right">
                      <span className={sender.duplicates > 0 ? 'text-amber-500' : ''}>{sender.duplicates}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-muted-foreground">No approved senders configured yet.</div>
        )}
      </div>

      <div className="border border-border rounded-md" data-testid="recent-receipts">
        <div className="p-6 border-b border-border">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Recent Receipts</h3>
        </div>
        {s.recent_receipts.length > 0 ? (
          <div className="divide-y divide-border">
            {s.recent_receipts.map((r) => (
              <div key={r.receipt_id} className="flex items-center justify-between px-6 py-3 hover:bg-secondary/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.company || 'Unknown'}</p>
                  <p className="text-xs text-muted-foreground font-mono">{r.invoice_number || 'N/A'}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="text-sm font-mono font-semibold">{r.total || 'N/A'}</p>
                  <p className="text-xs text-muted-foreground">{r.invoice_date || 'N/A'}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-muted-foreground">No receipts processed yet.</div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, variant, testId }) {
  return (
    <div className={`border border-border rounded-md p-5 ${getVariantClass(variant, value)}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-4 h-4 ${getIconClass(variant, value)}`} strokeWidth={1.5} />
        <TrendingUp className="w-3 h-3 text-muted-foreground" />
      </div>
      <p className="font-mono text-2xl font-bold">{value}</p>
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
